import { type RefObject, useLayoutEffect } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { CONTENT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';

type ScrollBehavior = ScrollIntoViewOptions['behavior'];

type UseListingPaginationScrollOptions = {
    scrollBehavior?: ScrollBehavior;
};

/**
 * Scrolls the listing anchor into view when pagination changes on the same pathname.
 */
export function useListingPaginationScroll(
    listingTopRef: RefObject<HTMLElement | null>,
    options?: UseListingPaginationScrollOptions
): void {
    const location = useLocation();
    const navigation = useNavigation();
    const scrollBehavior = options?.scrollBehavior ?? 'smooth';

    useLayoutEffect(() => {
        const getPage = (search: string) => new URLSearchParams(search).get(CONTENT_SEARCH_QUERY_PARAMS.PAGE) ?? '1';
        const isPageNavigation =
            navigation.state !== 'idle' &&
            navigation.location?.pathname === location.pathname &&
            getPage(navigation.location.search) !== getPage(location.search);
        if (!isPageNavigation) return;
        listingTopRef.current?.scrollIntoView({ block: 'start', behavior: scrollBehavior });
    }, [listingTopRef, location.pathname, location.search, navigation.location, navigation.state, scrollBehavior]);
}
